import React, { useState, useRef } from 'react';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';

export default function TextInputMuiMult({
  type = 'text',
  label,
  icon,
  required = false,
  readOnly = false,
  disabled = false,
  multiline = false,

  // single-value mode
  value,
  setValue,

  // multi-value mode
  multiple = false,
  values = [],
  setValues = () => {},

  // parsing
  delimiterRegex = /[,\n;]+/,
  allowDuplicates = false,
  maxItems, // optional limit
}) {
  const [showPassword, setShowPassword] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef(null);

  const renderLabel = required ? (
    <span>
      {label}{' '}
      <Typography component="span" sx={{ color: 'red' }}>
        *
      </Typography>
    </span>
  ) : (
    label
  );

  // ---------- helpers for multi ----------
  const normalize = (s) => String(s || '').trim();
  const splitToTokens = (txt) =>
    txt
      .split(delimiterRegex)
      .map(normalize)
      .filter(Boolean);

  const addTokens = (tokens) => {
    if (!tokens.length) return;
    let next = allowDuplicates
      ? [...values, ...tokens]
      : [...values, ...tokens.filter((t) => !values.includes(t))];

    if (typeof maxItems === 'number') next = next.slice(0, maxItems);
    setValues(next);
  };

  const handleKeyDown = (e) => {
    if (!multiple) return;
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const tokens = splitToTokens(inputValue);
      addTokens(tokens);
      setInputValue('');
    }
  };

  const handlePaste = (e) => {
    if (!multiple) return;
    const text = e.clipboardData.getData('text');
    const tokens = splitToTokens(text);
    if (tokens.length > 1) {
      e.preventDefault();
      addTokens(tokens);
      setInputValue('');
    }
  };

  // ---------- single-value mode ----------
  if (!multiple) {
    return (
      <TextField
        size="medium"
        disabled={disabled}
        type={type === 'password' && !showPassword ? 'password' : type}
        label={renderLabel}
        multiline={multiline}
        rows={multiline ? 3 : 1}
        variant="outlined"
        fullWidth
        value={value}
        onChange={(e) => setValue(e.target.value)}
        InputProps={{
          readOnly,
          startAdornment: icon && (
            <InputAdornment position="start">{icon}</InputAdornment>
          ),
          endAdornment:
            type === 'password' ? (
              <InputAdornment position="end">
                <IconButton onClick={() => setShowPassword((p) => !p)} edge="end">
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </IconButton>
              </InputAdornment>
            ) : null,
        }}
        sx={{
          
          fontSize: '14px',
          '& .MuiOutlinedInput-root': {
            
            '& fieldset': {
              borderRadius: '12px',
            },
          },
          '& .MuiInputLabel-root': {
            fontSize: '14px',
          },
          '& .MuiOutlinedInput-input': {
            padding: '12px 14px',
            fontSize: '14px',
          },
        }}
      />
    );
  }

  // ---------- multiple-values mode (chips) ----------
  return (
    <Autocomplete
      multiple
      freeSolo
      disabled={disabled}
      readOnly={readOnly}
      options={[]} // freeSolo, no predefined options
      value={values}
      onChange={(_, newValue) => {
        const flat = newValue.map(normalize).filter(Boolean);
        const dedup = allowDuplicates ? flat : Array.from(new Set(flat));
        setValues(typeof maxItems === 'number' ? dedup.slice(0, maxItems) : dedup);
      }}
      inputValue={inputValue}
      onInputChange={(_, newInp) => setInputValue(newInp)}
      filterSelectedOptions
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => (
          <Chip {...getTagProps({ index })} key={`${option}-${index}`} label={option} />
        ))
      }
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          label={renderLabel}
          variant="outlined"
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          InputProps={{
            ...params.InputProps,
            readOnly,
            startAdornment: (
              <>
                {icon && <InputAdornment position="start">{icon}</InputAdornment>}
                {params.InputProps.startAdornment}
              </>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': { 
             
              '& fieldset': { borderRadius: '8px' } 
            },
            '& .MuiInputLabel-root': {
              fontSize: '14px',
            },
            '& .MuiOutlinedInput-input': {
              padding: '12px 14px',
              fontSize: '14px',
            },
          }}
          placeholder="Saisir et valider par Entrée ou ,"
        />
      )}
    />
  );
}
